import Vue from 'vue'
import api from './api.js'
import router from './router.js'
import axios from 'axios'
import ErrorsMixin from '../mixins/ErrorsMixin'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'

const store = new Vue({

  mixins: [ErrorsMixin, AnalyticsMixin],

  data () {
    return {
      debug: true,
      user: {},
      organization: {
        organizationId: 0,
        providers: [],
        locations: [],
        visitTypes: [],
        paymentEnabled: false,
        pmType: ''
      },
      isAuthenticated: false,
      isLoggingIn: false,
      genderOptions: [
        { value: 1, text: 'Female' },
        { value: 2, text: 'Male' }
      ],
      appVersion: process.env.APP_VERSION || '0.0.0'
    }
  },

  watch: {
    organization (newValue, oldValue) {
      if (newValue.organizationId > 0 && oldValue.organizationId !== newValue.organizationId) {
        this.$log.info('isLoggingIn: ' + (this.isLoggingIn ? 'true' : 'false'))
        if (!this.isLoggingIn) {
          // Don't send org change event on login
          this.$emit('organization:changed', newValue)
        }
      }
    },

    isAuthenticated (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('user:authenticated', newValue)
      }
    }

  },

  computed: {
    userRoles () {
      if (!this.user.roles) {
        return []
      }

      return this.user.roles
    },

    isAdmin () {
      if (!this.user.roles) {
        return false
      }

      const adminRole = this.user.roles.filter(role => role === 'Admin').shift()
      if (adminRole) {
        return true
      }

      return false
    },

    isOrgAdmin () {
      if (!this.user.roles) {
        return false
      }

      if (this.isAdmin) {
        return true
      }

      const adminRole = this.user.roles.filter(role => role === 'OrganizationAdmin').shift()
      if (adminRole) {
        return true
      }

      return false
    },

    homePagePath () {
      const path = '/'

      if (!this.user.roles) {
        return path
      }

      const orgRole = this.user.roles.filter(role => role === 'OrganizationUser' || role === 'OrganizationAdmin').shift()
      if (orgRole) {
        return path
      }

      const payeeRole = this.user.roles.filter(role => role === 'OrganizationPayee').shift()
      if (payeeRole) {
        return '/payee-dashboard'
      }

      const employerRole = this.user.roles.filter(role => role === 'OrganizationEmployerUser').shift()
      if (employerRole) {
        return '/employer-dashboard'
      }

      return path
    }
  },

  methods: {
    userAuthenticated (newValue, storedOrg, completion) {
      this.$log.debug('userAuthenticated triggered with', newValue)
      this.isLoggingIn = true

      this.user = newValue
      this.setUserId(this.user.email)
      this.setCustomDimension(1, this.appVersion)
      this.trackEvent('Session', 'Authenticated')

      this.isAuthenticated = true
      axios.defaults.headers.common.Authorization = 'Bearer ' + this.user.idToken
      axios.defaults.headers.common['X-HH-Dashboard-OrganizationId'] = this.user.organizationId

      if (storedOrg) {
        this.$log.debug('using Org from local storage')
        this.setOrganization(storedOrg)
        this.$nextTick(() => {
          this.isLoggingIn = false
          if (completion) {
            completion()
          }
        })
      } else {
        api.get('/Dashboard/Organization').then(response => {

          response.data.providers.sort((lhs, rhs) => {
            return (lhs.displayName > rhs.displayName) ? 1 : ((rhs.displayName > lhs.displayName) ? -1 : 0)
          })
          response.data.locations.sort((lhs, rhs) => {
            return (lhs.name > rhs.name) ? 1 : ((rhs.name > lhs.name) ? -1 : 0)
          })
          if (response.data.visitTypes) {
            response.data.visitTypes.sort((lhs, rhs) => {
              return (lhs.name > rhs.name) ? 1 : ((rhs.name > lhs.name) ? -1 : 0)
            })
          }

          this.organization = response.data
          this.persistSession()
        }).catch(error => {
          if (error.response && error.response.status === 401) {
            this.logoutUser()
          } else {
            this.$log.error('Error attempting to load organization: ' + error.message, error)
          }
        }).then(() => {
          this.isLoggingIn = false
          if (completion) {
            completion()
          }
        })
      }
    },

    logoutUser () {
      this.trackEvent('Session', 'Logout')
      this.resetUserId()
      this.$log.debug('logout user triggered')

      delete api.defaults.headers.common.Authorization
      delete api.defaults.headers.common['X-HH-Dashboard-OrganizationId']
      this.user = {}
      this.organization = { providers: [], locations: [], visitTypes: [] }
      this.isAuthenticated = false
      localStorage.removeItem('user')
      localStorage.removeItem('organization')
      localStorage.removeItem('selectedInsuranceVerificationFilters')
      localStorage.removeItem('selectedBundleCandidateFilters')
      localStorage.removeItem('selectedBundlePaymentFilters')
      localStorage.removeItem('selectedBundleLibraryFilters')
      localStorage.removeItem('selectedEstimateFilters')
      localStorage.removeItem('selectedVisitFilters')
    },

    setOrganization (newValue) {
      this.$log.debug('setOrganization triggered with', newValue)
      this.trackEvent('Session', 'Organization', 'Changed', newValue.organizationId)

      newValue.providers.sort((lhs, rhs) => {
        return (lhs.displayName > rhs.displayName) ? 1 : ((rhs.displayName > lhs.displayName) ? -1 : 0)
      })
      newValue.locations.sort((lhs, rhs) => {
        return (lhs.name > rhs.name) ? 1 : ((rhs.name > lhs.name) ? -1 : 0)
      })
      if (newValue.visitTypes) {
        newValue.visitTypes.sort((lhs, rhs) => {
          return (lhs.name > rhs.name) ? 1 : ((rhs.name > lhs.name) ? -1 : 0)
        })
      }

      this.organization = newValue
      axios.defaults.headers.common['X-HH-Dashboard-OrganizationId'] = newValue.organizationId
      this.persistSession()
    },

    persistSession () {
      localStorage.setItem('user', JSON.stringify(this.user))
      localStorage.setItem('organization', JSON.stringify(this.organization))
    },

    restoreSession () {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        this.trackEvent('Session', 'Restore')
        const storedOrg = localStorage.getItem('organization')
        if (storedOrg) {
          this.userAuthenticated(JSON.parse(storedUser), JSON.parse(storedOrg))
        } else {
          this.userAuthenticated(JSON.parse(storedUser))
        }
      } else {
        this.logoutUser()
      }
    },

    logErrorResponse (component, name, error) {
      let errorMessage = ''
      if (error.response) {
        if (error.response.data) {
          this.setErrorsFromResponse(error.response.data)
          if (this.hasError('exception')) {
            errorMessage = this.errorForField('exception')
          }
        }
      }
      if (!errorMessage && error.message) {
        errorMessage = error.message
      }

      this.trackEvent(component, name, errorMessage)
    },

    canHandleNetworkError (error) {
      if (error.response && error.response.status === 401) {
        this.trackEvent('Session', 'Error', '401 Error')

        this.logoutUser()
        router.push('/login')
        return true
      }

      return false
    }
  }
})

export default store
